import { useEffect, useState } from "react";
import axios from "axios";
import { Eye, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export const LookupStats = ({ headers, onAddTracking }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/orders/lookups`, { headers }).then((r) => setData(r.data)).catch(() => setData({ week_total: 0, week_found: 0, week_failed: 0, unique_customers: 0, waiting: [] }));
  }, [headers]);

  if (!data) return <div data-testid="lookup-stats-loading" className="mb-6 h-20 rounded-3xl border border-[#e3e2dc] bg-[#faf9f6] flex items-center justify-center"><Loader2 size={16} className="animate-spin text-[#5f5e5a]" /></div>;
  const waiting = data.waiting.filter((w) => !w.shipped);

  return (
    <div data-testid="lookup-stats" className="mb-6 rounded-3xl border border-[#e3e2dc] bg-[#faf9f6] overflow-hidden">
      <div className="p-5 sm:p-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat testId="lookup-week-total" label="Status checks · 7 days" value={data.week_total} />
        <Stat testId="lookup-unique" label="Customers checking" value={data.unique_customers} />
        <Stat testId="lookup-waiting" label="Still waiting (unshipped)" value={waiting.length} pink={waiting.length > 0} />
        <Stat testId="lookup-failed" label="Failed lookups" value={data.week_failed} />
      </div>
      {data.waiting.length > 0 && (
        <>
          <button type="button" data-testid="lookup-toggle" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 px-5 sm:px-6 py-3 border-t border-[#e3e2dc] font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#5f5e5a] hover:text-[#0a0a0b] transition-colors">
            <span className="inline-flex items-center gap-2"><Eye size={13} /> Who checked their order this week</span>
            <span>{open ? "Hide" : "Show"}</span>
          </button>
          {open && (
            <ul data-testid="lookup-list" className="border-t border-[#e3e2dc] divide-y divide-[#e3e2dc]">
              {data.waiting.map((w) => (
                <li key={w.session_id} data-testid={`lookup-row-${w.session_id}`} className="px-5 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{w.customer_name || w.email} <span className="font-mono-d text-[10px] text-[#5f5e5a] ml-1">#{w.order_ref}</span></p>
                    <p className="text-xs text-[#5f5e5a] truncate">{w.email} · {w.product_name}{w.size ? ` · ${w.size}` : ""}{w.color ? ` · ${w.color}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-mono-d text-[10px] uppercase tracking-[0.15em] text-[#5f5e5a]">{w.checks}× · last {fmt(w.last_at)}</span>
                    {w.shipped ? (
                      <span className="font-mono-d text-[9px] uppercase tracking-[0.15em] text-white bg-[#0a0a0b] px-2.5 py-1 rounded-full">Shipped</span>
                    ) : (
                      <button type="button" data-testid={`lookup-ship-${w.session_id}`} onClick={() => onAddTracking(w.session_id)} className="h-8 px-3 rounded-full bg-[#ff2a85] hover:bg-[#e60067] text-white font-mono-d text-[9px] uppercase tracking-[0.2em] transition-colors">Add tracking</button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

const Stat = ({ label, value, pink, testId }) => (
  <div>
    <p className="font-mono-d text-[9px] uppercase tracking-[0.2em] text-[#5f5e5a]">{label}</p>
    <p data-testid={testId} className={`font-display font-extrabold text-2xl sm:text-3xl mt-1 ${pink ? "text-[#ff2a85]" : ""}`}>{value}</p>
  </div>
);
